import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { SvgComponent } from '@components/svg.component';
import { TerminalOrderModesComponent } from '@components/terminal-order-modes/terminal-order-modes.component';
import { TerminalUserPermissionsComponent } from '@components/terminal-user-permissions/terminal-user-permissions.component';
import { HistoryService } from '@services/history.service';

const ACTIVE_SECTION_STORAGE_KEY = 'admin_settings_active_section';

export type AdminSettingsSectionKey = 'terminal-permissions' | 'order-modes';

interface AdminSettingsSection {
  key: AdminSettingsSectionKey;
  labelKey: string;
  titleKey: string;
  descriptionKey: string;
}

@Component({
  selector: 'app-admin-settings-page',
  imports: [
    TranslateModule,
    SvgComponent,
    TerminalUserPermissionsComponent,
    TerminalOrderModesComponent,
  ],
  templateUrl: './admin-settings-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { class: 'block h-full' },
})
export class AdminSettingsPageComponent {
  private readonly historyService = inject(HistoryService);

  protected readonly sections: AdminSettingsSection[] = [
    {
      key: 'terminal-permissions',
      labelKey: 'admin_settings.sections.terminal_permissions',
      titleKey: 'admin_settings.terminal_permissions.title',
      descriptionKey: 'admin_settings.terminal_permissions.description',
    },
    {
      key: 'order-modes',
      labelKey: 'admin_settings.sections.order_modes',
      titleKey: 'admin_settings.order_modes.title',
      descriptionKey: 'admin_settings.order_modes.description',
    },
  ];

  protected readonly activeSection = signal<AdminSettingsSectionKey>(this.#readStoredSection());

  protected readonly activeSectionData = computed(
    () => this.sections.find((section) => section.key === this.activeSection()) ?? null,
  );

  goBack(): void {
    this.historyService.back();
  }

  selectSection(key: AdminSettingsSectionKey): void {
    this.activeSection.set(key);
    try {
      sessionStorage.setItem(ACTIVE_SECTION_STORAGE_KEY, key);
    } catch {
      return;
    }
  }

  #readStoredSection(): AdminSettingsSectionKey {
    try {
      const key = sessionStorage.getItem(ACTIVE_SECTION_STORAGE_KEY);
      return this.sections.find((section) => section.key === key)?.key ?? 'terminal-permissions';
    } catch {
      return 'terminal-permissions';
    }
  }
}
